import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PACK_LABELS = { sports: "Sports", stocks: "Stocks", options: "Options" };
const PACK_COLORS = { sports: "#007AFF", stocks: "#F59E0B", options: "#A855F7" };

const CADENCE_LABEL = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", annual: "Annual" };
const CADENCE_SUFFIX = { weekly: "/wk", monthly: "/mo", quarterly: "/qtr", annual: "/yr" };

function dollars(cents) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function effectiveWeekly(cents, cadence) {
  if (cadence === "weekly") return cents;
  if (cadence === "monthly") return cents / 4.33;
  if (cadence === "quarterly") return cents / 13;
  if (cadence === "annual") return cents / 52;
  return cents;
}

function PlanCard({ plan, cadence, current, highlight }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const price = plan.prices[cadence];
  if (!price) return null;
  const baseWeekly = plan.prices.weekly?.amount_cents || 2500;
  const effWeekly = effectiveWeekly(price.amount_cents, cadence);
  const savePct = cadence !== "weekly" ? Math.round((1 - effWeekly / baseWeekly) * 100) : 0;
  const isAllAccess = plan.tier === "all_access";
  const accent = isAllAccess ? "#22C55E" : plan.included.length === 2 ? "#EC4899" : PACK_COLORS[plan.included[0]] || "#888";

  const onPick = async () => {
    if (!user) { navigate("/signup?next=/pricing"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pricing/checkout-session", { price_id: price.price_id, trial_days: 7 });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`relative bg-[#0F0F0F] border rounded-sm p-6 flex flex-col ${highlight ? "ring-2 ring-offset-2 ring-offset-black" : ""}`}
      style={{
        borderColor: highlight ? accent : "rgba(255,255,255,0.1)",
        boxShadow: highlight ? `0 0 24px ${accent}33` : undefined,
        ringColor: highlight ? accent : undefined,
      }}
      data-testid={`plan-card-${plan.plan_key}`}
    >
      {highlight && (
        <div className="absolute -top-3 right-4 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold text-black rounded-sm" style={{ background: accent }}>
          Most Popular
        </div>
      )}
      {current && (
        <div className="absolute -top-3 left-4 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold text-white rounded-sm bg-[#22C55E]">
          Current Plan
        </div>
      )}

      <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: accent }}>{plan.tier === "single" ? "Single" : plan.tier === "bundle" ? "Bundle · save ~30%" : "All Access · save ~40%"}</div>
      <h3 className="font-display text-3xl mt-1.5 mb-2">{plan.name}</h3>

      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-display text-5xl" style={{ color: accent }} data-testid={`plan-price-${plan.plan_key}`}>
          {dollars(price.amount_cents)}
        </span>
        <span className="text-zinc-500 text-sm">{CADENCE_SUFFIX[cadence]}</span>
      </div>
      {savePct > 5 && (
        <div className="text-[10px] uppercase tracking-widest text-[#22C55E] font-bold mb-3">
          Save {savePct}% vs weekly · ~${(effWeekly / 100).toFixed(2)}/wk effective
        </div>
      )}
      {savePct <= 5 && <div className="mb-3" />}

      <ul className="text-sm text-zinc-300 space-y-1.5 mb-5">
        {["sports", "stocks", "options"].map((p) => (
          <li key={p} className="flex items-center gap-2">
            {plan.included.includes(p)
              ? <Check size={14} weight="bold" className="text-[#22C55E]" />
              : <X size={14} className="text-zinc-700" />}
            <span className={plan.included.includes(p) ? "text-zinc-200" : "text-zinc-600 line-through"}>
              {PACK_LABELS[p]} Picks
            </span>
          </li>
        ))}
      </ul>

      <p className="text-[11px] text-zinc-500 italic leading-relaxed mb-5 flex-1">{plan.description}</p>

      <button
        onClick={onPick}
        disabled={busy}
        data-testid={`plan-cta-${plan.plan_key}`}
        className="w-full py-3 font-bold text-xs uppercase tracking-widest rounded-sm transition-all disabled:opacity-40 hover:shadow-lg"
        style={{ background: accent, color: isAllAccess ? "#000" : "#fff" }}
      >
        {busy ? "…" : current ? "Current plan" : "Start 7-day free trial"}
        {!busy && !current && <ArrowRight size={12} className="inline ml-2" />}
      </button>
    </motion.div>
  );
}

export default function Pricing() {
  const [cadence, setCadence] = useState("monthly");
  const [pricing, setPricing] = useState(null);
  const [access, setAccess] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data));
    if (user) api.get("/me/access").then((r) => setAccess(r.data)).catch(() => {});
  }, [user]);

  if (!pricing) return <div className="text-zinc-500 font-mono-d py-20 text-center">Loading pricing…</div>;

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#22C55E] mb-3">Pricing · 7-day free trial on every plan</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">Pick your plan.</h1>
        <p className="text-zinc-400 text-sm max-w-2xl mx-auto mt-4 leading-relaxed">
          One subscription, full access to the picks you choose. Cancel anytime. Bundles save up to 40% vs buying individually.
        </p>
      </div>

      {/* Cadence toggle */}
      <div className="flex justify-center mb-10">
        <div className="inline-flex border border-white/15 rounded-sm overflow-hidden" data-testid="pricing-cadence-tabs">
          {["weekly", "monthly", "quarterly", "annual"].map((c) => (
            <button
              key={c}
              onClick={() => setCadence(c)}
              data-testid={`pricing-cadence-${c}`}
              className={`px-5 py-2 text-xs uppercase tracking-widest font-bold transition-colors ${
                cadence === c ? "bg-white text-black" : "text-zinc-400 hover:text-white"
              } ${c !== "weekly" ? "border-l border-white/15" : ""}`}
            >
              {CADENCE_LABEL[c]}
              {c === "annual" && <span className="ml-1.5 text-[9px] text-[#22C55E]">−38%</span>}
              {c === "quarterly" && <span className="ml-1.5 text-[9px] text-[#22C55E]">−26%</span>}
              {c === "monthly" && <span className="ml-1.5 text-[9px] text-[#22C55E]">−18%</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Single products */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Single products</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {pricing.plans.filter((p) => p.tier === "single").map((p) => (
            <PlanCard
              key={p.plan_key}
              plan={p}
              cadence={cadence}
              current={access?.subscription_plan === p.plan_key}
              highlight={p.plan_key === "sports"}
            />
          ))}
        </div>
      </div>

      {/* Bundles */}
      <div className="mb-6 mt-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Two-pack bundles · save ~30%</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {pricing.plans.filter((p) => p.tier === "bundle").map((p) => (
            <PlanCard
              key={p.plan_key}
              plan={p}
              cadence={cadence}
              current={access?.subscription_plan === p.plan_key}
            />
          ))}
        </div>
      </div>

      {/* All access */}
      <div className="mt-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">All Access · save ~40%</div>
        <div className="grid grid-cols-1 max-w-md mx-auto">
          {pricing.plans.filter((p) => p.tier === "all_access").map((p) => (
            <PlanCard
              key={p.plan_key}
              plan={p}
              cadence={cadence}
              current={access?.subscription_plan === p.plan_key}
              highlight
            />
          ))}
        </div>
      </div>

      {/* Trust strip */}
      <div className="mt-14 bg-[#121212] border border-white/10 rounded-sm p-6 max-w-3xl mx-auto flex items-start gap-4">
        <ShieldCheck size={28} weight="fill" className="text-[#22C55E] shrink-0" />
        <div>
          <div className="font-display text-xl mb-1">7-day free trial. No card charged until day 8.</div>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Cancel anytime from your account. Picks are locked + timestamped before they go live — every result is auditable and immutable.
          </p>
        </div>
      </div>

      <div className="text-center mt-10 text-[11px] uppercase tracking-widest text-zinc-600">
        21+ · Not financial advice · <Link to="/contact" className="underline">Questions? Contact us</Link>
      </div>
    </div>
  );
}
